/**
 * SE-1765: per-network feature-flag state for the current user.
 *
 * A single-instance slice (not keyed by programIds like
 * `networkProgramsSlice`) because there's exactly one answer per
 * logged-in user — "which features are enabled on any of my programs'
 * networks, and per-program which ones apply." The `network-features`
 * Lambda pre-aggregates for us; this slice just caches the response.
 *
 * The router and top-nav read `aggregate` to decide whether to show
 * Coaching / Rotations tabs. The invitation form reads `byProgramId` to
 * decide whether `Coach` / `RotationDirector` are offerable for a
 * specific target program.
 */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { QueryStatus } from "../../app/types";
import { RootState } from "../../app/store";

export type NetworkFeatureFlags = {
  coaching: boolean;
  rotations: boolean;
};

export type NetworkFeaturesResponse = {
  aggregate: NetworkFeatureFlags;
  byProgramId: Record<string, NetworkFeatureFlags>;
};

interface NetworkFeaturesState {
  data: NetworkFeaturesResponse | null;
  status: QueryStatus;
  error: string | undefined;
}

const initialState: NetworkFeaturesState = {
  data: null,
  status: "idle",
  error: undefined,
};

const emptyFlags: NetworkFeatureFlags = {
  coaching: false,
  rotations: false,
};

export const networkFeaturesSlice = createSlice({
  name: "networkFeatures",
  initialState,
  reducers: {
    setNetworkFeatures: (
      state,
      action: PayloadAction<NetworkFeaturesResponse>,
    ) => {
      state.data = action.payload;
    },
    setNetworkFeaturesStatus: (state, action: PayloadAction<QueryStatus>) => {
      state.status = action.payload;
    },
    setNetworkFeaturesError: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.error = action.payload;
    },
    clearNetworkFeatures: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = undefined;
    },
    // SE-1765: on-demand per-program merge for the invitation-form case.
    // A SysAdmin (or anyone picking a program they aren't a member of)
    // won't have the target program in `byProgramId` from the initial
    // caller-membership fetch, so the form fetches for that specific
    // programId and folds it in here. The aggregate is NOT recomputed
    // (aggregate is scoped to the caller's own memberships for nav
    // visibility; a program the caller only invites INTO doesn't grow
    // the caller's own aggregate).
    mergeProgramFeatures: (
      state,
      action: PayloadAction<{
        programId: string;
        flags: NetworkFeatureFlags;
      }>,
    ) => {
      const { programId, flags } = action.payload;
      if (!state.data) {
        state.data = {
          aggregate: emptyFlags,
          byProgramId: { [programId]: flags },
        };
        return;
      }
      state.data.byProgramId[programId] = flags;
    },
  },
});

export const {
  setNetworkFeatures,
  setNetworkFeaturesStatus,
  setNetworkFeaturesError,
  clearNetworkFeatures,
  mergeProgramFeatures,
} = networkFeaturesSlice.actions;

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectNetworkFeaturesState = (state: RootState) =>
  state.networkFeatures;

/**
 * Aggregate flags across every network the caller has a program in.
 * Returned as a stable no-flags object when the fetch hasn't landed yet,
 * so consumers can render "hidden" without null-checks.
 */
export const selectAggregateFeatures = (state: RootState): NetworkFeatureFlags =>
  state.networkFeatures.data?.aggregate ?? emptyFlags;

/**
 * Flags for one specific program the caller is in. Returns the empty
 * flags object when the program isn't in the map (either the fetch
 * hasn't landed, or the caller isn't in that program) — safe default:
 * "no features," which hides affordances rather than showing them.
 */
export const selectFeaturesForProgram =
  (programId: string | null | undefined) =>
  (state: RootState): NetworkFeatureFlags => {
    if (!programId) return emptyFlags;
    return state.networkFeatures.data?.byProgramId[programId] ?? emptyFlags;
  };

/**
 * The whole (programId -> flags) map. Empty object when the fetch hasn't
 * landed. Used by pages that need to filter a list of programs by a
 * feature (e.g. Coaching / Rotations pickers): calling
 * `selectFeaturesForProgram` in a loop would need one `useAppSelector`
 * per iteration, which the rules of hooks disallow. Read the map once,
 * index into it inline.
 */
export const selectNetworkFeaturesByProgramId = (
  state: RootState,
): Record<string, NetworkFeatureFlags> =>
  state.networkFeatures.data?.byProgramId ?? {};

export default networkFeaturesSlice.reducer;
